<template>
    <div :class="`phone-number phone-number-align-${align}`">
        <label for="phone-number" class="field-label">
            {{ fieldPhone }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        <div :class="['input-wrapper', { 'error': shouldShowError, 'filled': phoneNumberValue }]">
            <input 
                type="tel" 
                id="phone-number" 
                name="phone-number" 
                v-model="phoneNumberValue" 
                @input="emitValues"
                @focus="onFocus"
                @blur="onBlur"
                :class="{ 'has-value': phoneNumberValue }"
            />
            <label for="phone-number" class="floating-label">{{ masked }}</label>
            <div class="input-border"></div>
        </div>
        <div v-if="shouldShowError" class="error-message">
            {{ fieldPhone }} alanı zorunludur
        </div>
    </div>
</template>

<script>
export default {
    name: "FormPhoneNumber",
    props: {
        fieldPhone: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            required: true,
        },
        masked: {
            type: String,
            required: true,
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        showError: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, String],
            default: () => ({ phoneNumber: "" }),
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        fieldId: {
            type: String,
            required: true,
        }
    },
    data() {
        return {
            phoneNumberValue: (typeof this.value === 'object' && this.value !== null) 
                ? this.value.phoneNumber || ""
                : "",
        };
    },
    computed: {
        hasValidationError() {
            return this.isRequired && (!this.phoneNumberValue || this.phoneNumberValue.trim() === '');
        },
        shouldShowError() {
            return this.showError && this.hasValidationError;
        }
    },
    watch: {
        value: {
            handler(newValue) {
                this.phoneNumberValue = (typeof newValue === 'object' && newValue !== null) 
                    ? newValue.phoneNumber || ""
                    : "";
            },
            deep: true,
        },
        phoneNumberValue: {
            handler() {
                this.emitValues();
            }
        },
    },
    methods: {
        emitValues() {
            const phoneData = {
                phoneNumber: this.phoneNumberValue,
                fieldPhone: this.fieldPhone,
                masked: this.masked,
            };
            
            console.log('🔍 Phone validation:', {
                fieldId: this.fieldId,
                isRequired: this.isRequired,
                phoneNumberValue: this.phoneNumberValue,
                hasValidationError: this.hasValidationError,
                shouldShowError: this.shouldShowError
            });
            
            this.$emit("input", phoneData);
            this.$emit("validate", {
                fieldId: this.fieldId,
                hasError: this.hasValidationError,
            });
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(phoneData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.phoneNumberValue = parsedData.phoneNumber;
            }
        },
        onFocus(event) {
            event.target.parentElement.classList.add('focused');
        },
        onBlur(event) {
            event.target.parentElement.classList.remove('focused');
        }
    },
    mounted() {
        console.log('🔴 PhoneNumber mounted:', {
            fieldId: this.fieldId,
            isRequired: this.isRequired,
            value: this.value,
            phoneNumberValue: this.phoneNumberValue,
            fieldPhone: this.fieldPhone
        });
        
        const hasSlugValue = this.value && typeof this.value === 'object' && this.value.phoneNumber;
        
        if (!hasSlugValue) {
            this.loadValues();
        }
        
        this.emitValues();
    }
};
</script>

<style lang="scss" scoped>
/* Base Phone Number Styles */
.phone-number {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .field-label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        line-height: 1.4;
        letter-spacing: -0.01em;
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.125rem;
            font-weight: 700;
        }
    }

    .input-wrapper {
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        min-width: 0; // Flex item için minimum genişlik

        input {
            width: 100%;
            padding: clamp(0.6rem, 1.5vw, 0.75rem) clamp(0.6rem, 1.2vw, 0.75rem);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.4;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 0.375rem;
            box-sizing: border-box;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-family: inherit;
            backdrop-filter: blur(10px);
            
            &::placeholder {
                color: transparent;
            }
            
            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 
                    0 0 0 3px rgba(99, 102, 241, 0.1),
                    0 4px 12px rgba(0, 0, 0, 0.05);
                transform: translateY(-1px);
            }
            
            &:hover:not(:focus) {
                border-color: rgba(107, 114, 128, 0.4);
                background: rgba(255, 255, 255, 0.85);
            }
        }

        .floating-label {
            position: absolute;
            left: clamp(0.6rem, 1.2vw, 0.75rem);
            top: 50%;
            transform: translateY(-50%);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            color: rgba(107, 114, 128, 0.7);
            pointer-events: none;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            background: transparent;
            padding: 0 0.25rem;
            font-weight: 500;
        }

        .input-border {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, var(--primary, #6366f1), var(--secondary, #8b5cf6));
            border-radius: 1px;
            transform: scaleX(0);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        &.focused .floating-label,
        &.filled .floating-label,
        input:focus ~ .floating-label,
        input.has-value ~ .floating-label {
            top: 0;
            transform: translateY(-50%);
            font-size: clamp(0.7rem, 1.6vw, 0.8rem);
            color: var(--primary, #6366f1);
            font-weight: 600;
            background: rgba(255, 255, 255, 0.9);
        }

        &.focused .input-border,
        input:focus ~ .input-border {
            transform: scaleX(1);
        }

        &.error {
            input {
                border-color: #ef4444;
                background: rgba(254, 242, 242, 0.8);
                
                &:focus {
                    border-color: #ef4444;
                    box-shadow: 
                        0 0 0 3px rgba(239, 68, 68, 0.1),
                        0 4px 12px rgba(239, 68, 68, 0.1);
                }
            }
            
            .floating-label {
                color: #ef4444;
            }
            
            .input-border {
                background: #ef4444;
            }
        }
    }

    .error-message {
        color: #ef4444;
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        margin-top: clamp(0.375rem, 1vw, 0.5rem);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.375rem;
        
        &::before {
            content: '⚠';
            font-size: 0.875em;
        }
    }
}

/* Left Alignment - Label solda, input sağda */
.phone-number-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    text-align: left;

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: left;
    }

    .input-wrapper {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
    }

    .input-wrapper {
        width: 100%;
        }
    }
}

/* Right Alignment - Label solda sağa yaslanmış, input sağda */
.phone-number-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: right;
    }

    .input-wrapper {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            text-align: left;
    }

    .input-wrapper {
        width: 100%;
    }
}
}

/* Center/Top Alignment (Default) */
.phone-number-align-top {
    text-align: left;
    flex-direction: column;
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .phone-number {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .field-label {
            font-size: clamp(0.9rem, 2vw, 1rem);
            margin-bottom: clamp(0.375rem, 1vw, 0.5rem);
    }

    .input-wrapper {
        input {
                padding: clamp(0.75rem, 2vw, 0.875rem) clamp(0.625rem, 1.5vw, 0.75rem);
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                border-radius: 0.5rem;
            }
            
            .floating-label {
                left: clamp(0.625rem, 1.5vw, 0.75rem);
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                
                &.focused,
                &.filled {
                    font-size: clamp(0.75rem, 1.8vw, 0.8rem);
                }
            }
        }

        .error-message {
            font-size: clamp(0.8rem, 1.8vw, 0.85rem);
            margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
    }
}

@media (max-width: 480px) {
    .phone-number {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .input-wrapper {
            input {
                padding: 0.75rem 0.625rem;
                font-size: 0.875rem;
            }
            
            .floating-label {
                left: 0.625rem;
                font-size: 0.875rem;
                
                &.focused,
                &.filled {
                    font-size: 0.75rem;
                }
            }
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .phone-number,
    .input-wrapper,
    .input-wrapper input,
    .floating-label,
    .input-border {
        transition: none;
    }
    
    .input-wrapper input:focus {
        transform: none;
    }
}

/* High Performance Mode */
.input-wrapper {
    will-change: transform;
    
    input {
        will-change: transform, box-shadow;
    }
    
    .floating-label {
        will-change: transform, color, font-size;
        }
    
    .input-border {
        will-change: transform;
    }
}
</style>